import {
  PriceDisplay,
  PriceDisplayEnum,
  SortOptionEnum,
} from 'src/components/price-list/types';
import { Price } from 'src/types';
import { IListPrices, IPriceListPanelProps, ICommonListProps } from '../types';

export function getListLimits(prices: IListPrices, isGroupedList: boolean) {
  const mainLimit = isGroupedList ? 10 : 15;
  const secondaryLimit = 5;
  const mainListLimit =
    prices.mainList.length <= mainLimit ? prices.mainList.length : mainLimit;
  const secondaryListLimit =
    prices.secondaryList && prices.secondaryList.length <= secondaryLimit
      ? prices.secondaryList.length
      : secondaryLimit;
  return { mainListLimit, secondaryListLimit };
}

export function getListPrices(
  sortedPrices: Price[] | null | undefined,
  splitList: boolean,
  currentSorting: SortOptionEnum,
  priceDisplay: PriceDisplay
): IListPrices {
  if (!splitList) {
    return { mainList: sortedPrices || [], secondaryList: null };
  }
  switch (currentSorting) {
    case SortOptionEnum.RECOMMENDED:
    case SortOptionEnum.PRICEANDSTOCKSTATUS:
      return getListsSplittedByUncertainData(sortedPrices, priceDisplay);
    case SortOptionEnum.FEATURED:
      return getListsSplittedByFeatured(sortedPrices, priceDisplay);
    default:
      return getListsSplittedByShippingInfo(sortedPrices);
  }
}

export const getListsSplittedByUncertainData = (
  sortedPrices: Price[] | null | undefined,
  priceDisplay: PriceDisplay
): IListPrices => {
  const showPricesWithShipping =
    priceDisplay === PriceDisplayEnum.PRICE_INCLUDES_SHIPPING;
  if (showPricesWithShipping) {
    return {
      mainList: filterOnCertainDataIncShipping(sortedPrices),
      secondaryList: filterOnUncertainDataIncShipping(sortedPrices),
    };
  }
  return {
    mainList: filterOnCertainData(sortedPrices),
    secondaryList: filterOnUncertainData(sortedPrices),
  };
};

export const getListsSplittedByFeatured = (
  sortedPrices: Price[] | null | undefined,
  priceDisplay: PriceDisplay
): IListPrices => {
  const showPricesWithShipping =
    priceDisplay === PriceDisplayEnum.PRICE_INCLUDES_SHIPPING;
  if (showPricesWithShipping) {
    return {
      mainList: filterOnFeaturedIncShipping(sortedPrices),
      secondaryList: filterOnUnfeaturedIncShipping(sortedPrices),
    };
  }
  return {
    mainList: filterOnFeatured(sortedPrices),
    secondaryList: filterOnUnfeatured(sortedPrices),
  };
};

export const getListsSplittedByShippingInfo = (
  sortedPrices: Price[] | null | undefined
): IListPrices => {
  return {
    mainList: filterOnShippingInfo(sortedPrices),
    secondaryList: filterOnMissingShippingInfo(sortedPrices),
  };
};

const filterOnShippingInfo = (
  sortedPrices: Price[] | null | undefined
): Price[] => {
  return sortedPrices?.filter(f => f.price?.inclShipping) || [];
};

const filterOnMissingShippingInfo = (
  sortedPrices: Price[] | null | undefined
): Price[] => {
  return sortedPrices?.filter(f => !f.price?.inclShipping) || [];
};

const filterOnCertainData = (
  sortedPrices: Price[] | null | undefined
): Price[] => {
  return sortedPrices?.filter(f => !f.uncertainData) || [];
};

const filterOnUncertainData = (
  sortedPrices: Price[] | null | undefined
): Price[] => {
  return sortedPrices?.filter(f => f.uncertainData) || [];
};

const filterOnCertainDataIncShipping = (
  sortedPrices: Price[] | null | undefined
): Price[] => {
  return (
    sortedPrices?.filter(f => !f.uncertainData && f.price?.inclShipping) || []
  );
};

const filterOnUncertainDataIncShipping = (
  sortedPrices: Price[] | null | undefined
): Price[] => {
  return (
    sortedPrices?.filter(f => f.uncertainData || !f.price?.inclShipping) || []
  );
};

const filterOnFeatured = (
  sortedPrices: Price[] | null | undefined
): Price[] => {
  return sortedPrices?.filter(f => f.store?.featured) || [];
};

const filterOnUnfeatured = (
  sortedPrices: Price[] | null | undefined
): Price[] => {
  return sortedPrices?.filter(f => !f.store?.featured) || [];
};

const filterOnFeaturedIncShipping = (
  sortedPrices: Price[] | null | undefined
): Price[] => {
  return (
    sortedPrices?.filter(f => f.store?.featured && f.price?.inclShipping) || []
  );
};

const filterOnUnfeaturedIncShipping = (
  sortedPrices: Price[] | null | undefined
): Price[] => {
  return (
    sortedPrices?.filter(f => !f.store?.featured || !f.price?.inclShipping) ||
    []
  );
};

export const getCommonListProps = (
  props: IPriceListPanelProps
): ICommonListProps => {
  return {
    productId: props.productId,
    productName: props.productName,
    media: props.media,
    brand: props.brand,
    campaign: props.campaign,
    priceDisplay: props.priceDisplay,
    currentSorting: props.currentSorting,
    adOffset: props.adOffset,
  };
};
